import {customElement, property, query} from 'lit/decorators.js';
import type {Model} from './architecture.js';
import {Msg, main} from './architecture.js';
import type {Constructor} from '#lit/types/classes.js';
import {FlywheelComponent} from '#lit/lib/component.js';
import {ImmutableComponent} from '#lit/components/mixins/immutable_component/index.js';
import type FlySelect from '../../select/index.js';
import type {AudienceFrequencyEnum} from '#lit/types/journey/audience_frequency.js';
import {isAudienceFrequency} from '#lit/types/journey/audience_frequency.js';

import styles from './style.scss';
import selectStyles from '#lit/components/base/select/style.scss';
import {consume} from '@lit-labs/context';
import {AudienceContext} from '#lit/contexts/audience.js';

export const SCHEDULE_FREQUENCY_ELEMENT_TAG = 'fly-schedule-frequency';

/**
 * Handle frequency scheduling with the recurring defaults.
 *
 * @fires FlyScheduleFrequencyInput#frequency:change
 */
@customElement(SCHEDULE_FREQUENCY_ELEMENT_TAG)
export default class FlyScheduleFrequencyInput extends ImmutableComponent<
  Constructor<FlywheelComponent>,
  Model,
  Msg
>({component: FlywheelComponent, main}) {
  @consume({context: AudienceContext})
  @property({type: Object})
  get audience() {
    return this.model.audience ?? null;
  }
  set audience(value) {
    this.handleUpdate(Msg.setAudience(value));
  }

  @property({type: String})
  form = '';

  @property({type: String})
  name = '';

  @property({
    type: String,
    converter(value) {
      return isAudienceFrequency(value) ? value : '';
    },
  })
  get value() {
    return this.model.value;
  }
  set value(value) {
    this.handleUpdate(Msg.setValue(value));
  }

  @property({
    type: String,
    attribute: 'default-value',
  })
  defaultValue = '';

  @property({type: Boolean, reflect: true})
  get hidden() {
    return this.model.hidden;
  }
  set hidden(value) {
    this.handleUpdate(Msg.setHidden(value));
  }

  @property({type: Boolean, reflect: true})
  get required() {
    return this.model.required;
  }
  set required(value) {
    this.handleUpdate(Msg.setHidden(value));
  }

  @property({type: Boolean, reflect: true})
  get disabled() {
    return this.model.disabled;
  }
  set disabled(value) {
    this.handleUpdate(Msg.setDisabled(value));
  }

  @property({
    type: Array,
    converter(value) {
      if (Array.isArray(value)) {
        return value.filter(isAudienceFrequency);
      }
      return isAudienceFrequency(value) ? [value] : [];
    },
  })
  get options() {
    return this.model.options;
  }
  set options(value) {
    this.handleUpdate(Msg.setOptions(value));
  }

  get internals() {
    return this._internals;
  }

  @query('fly-select')
  $select?: FlySelect;

  private _internals = this.attachInternals();

  static get formAssociated() {
    return true;
  }

  static get styles() {
    return [styles, selectStyles];
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [SCHEDULE_FREQUENCY_ELEMENT_TAG]: FlyScheduleFrequencyInput;
  }

  interface HTMLElementEventMap {
    'frequency:change': CustomEvent<{value: AudienceFrequencyEnum}>;
  }
}
